import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  IconButton,
  Text,
  VStack,
  Button,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { pxToRem } from "../../utils/editor.ts";
import { PaperService } from "../../services/paperService";
import { ClientError } from "../../utils/clientError";
import { useRecoilValue } from "recoil";
import { selectedPaperId } from "../../atoms/rootAtom";
import useOutlineStore from "../../stores/modules/outline.ts";

type HistoryItem = {
  id: string;
  name: string;
  created_at: string;
};

type HistoryDetail = {
  id: string;
  nodes: any[];
  created_at: string;
};

type HistoryByDate = {
  date: string;
  histories: HistoryItem[];
  isOpen: boolean;
};

type HistoryPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  isOpen,
  onClose,
 
}) => {
  const paperId = useRecoilValue(selectedPaperId);
  const [loading, setLoading] = useState<boolean>(false);
  const [historyByDate, setHistoryByDate] = useState<HistoryByDate[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<HistoryDetail | null>(
    null
  );
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(
    null
  );
  const [restoring, setRestoring] = useState<boolean>(false);
  const toast = useToast();
  const { outline, setOutline, setIsEditable, editData, setEditData, setCurrentSectionIndex, setOutlineIsChange } = useOutlineStore();

  useEffect(() => {
    if (isOpen && paperId) {
      fetchHistories();
      setEditData(outline)
    }
  }, [isOpen, paperId]);

  const fetchHistories = async () => {
    if (!paperId) return;

    try {
      setLoading(true);
      const response = await PaperService.getPaperOutlineHistory(paperId);
      const groupedHistories: { [key: string]: HistoryItem[] } = {};

      (response?.histories || []).forEach((history: HistoryItem) => {
        const date = new Date(history.created_at).toLocaleDateString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
        });
        if (!groupedHistories[date]) {
          groupedHistories[date] = [];
        }
        groupedHistories[date].push(history);
      });

      const historyByDateArray: HistoryByDate[] = Object.keys(
        groupedHistories
      ).map((date) => ({
        date,
        histories: groupedHistories[date].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ), // 按时间降序排列同一天的历史记录
        isOpen: false,
      }));

      historyByDateArray.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setHistoryByDate(historyByDateArray);
    } catch (error) {
      new ClientError(error).toast();
    } finally {
      setLoading(false);
    }
  };

  const toggleDateGroup = (index: number) => {
    const updatedHistory = [...historyByDate];
    updatedHistory[index].isOpen = !updatedHistory[index].isOpen;
    setHistoryByDate(updatedHistory);
  };

  
  // 修改 HistoryPanel 组件，更新 handleSelectHistory 方法
  const handleSelectHistory = async (historyId: string) => {
    if (selectedHistoryId === historyId) return;
  
    try {
      setSelectedHistoryId(historyId);
 
      // 获取历史详情
      const historyDetail = await PaperService.getHistoryDetail(historyId);
      setSelectedHistory(historyDetail.history);
      
      // 更新 outline store 中的状态
      setIsEditable(false); // 设置为不可编辑
      setOutline(historyDetail.history); // 保存历史数据
      setCurrentSectionIndex(0)
    } catch (error) {
      new ClientError(error).toast();
    }
  };

  const handleClose = () => {
    onClose();
   
    setSelectedHistory(null);
    setSelectedHistoryId(null);
    
    // 重置 outline store 中的状态
    setIsEditable(true); // 恢复可编辑状态
    setOutline(editData); // 清空历史数据
    setCurrentSectionIndex(0)
  };
  
  const handleRestoreHistory = async () => {
    if (!selectedHistory || !paperId || !editData) return;

    try {
      setRestoring(true);

      await PaperService.restorePaperOutline(paperId, {
        current_outline: editData,
        target_history_id: selectedHistory.id,
      });
      setIsEditable(true); // 恢复可编辑状态
      setCurrentSectionIndex(0)
      setOutline(outline);
      setOutlineIsChange(true)
      // 关闭历史面板并重置编辑器状态
      onClose();
      setSelectedHistory(null);
      setSelectedHistoryId(null);
      toast({
        title: "success",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      new ClientError(error).toast();
    } finally {
      setRestoring(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (!isOpen) return null;

  return (
    <Card
      bg={"#ffffff"}
      border={"1px solid #E2E8F0"}
      borderRadius={"25px"}
      boxShadow={"none"}
      h={"100%"}
      p={3}
      overflow={"hidden"}
    >
      <CardHeader px={4} pt={3} pb={2}>
        <Flex
          flexDir={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Heading size="sm" fontSize={16} mt={2}>
            Version History
          </Heading>
          <Flex>
            <Button
              colorScheme="#1D8AFF"
              size="sm"
              mr={10}
              isLoading={restoring}
              onClick={handleRestoreHistory}
              borderRadius="md"
              variant={"outline"}
              color="#1D8AFF"
              isDisabled={!selectedHistory}
            >
              Restore
            </Button>
            <IconButton
              onClick={handleClose}
              aria-label="close-history"
              color={"#8391BC"}
              icon={<CloseIcon />}
              size={"sm"}
              mt={-1}
              mr={-4}
              variant={"ghost"}
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody overflow={"auto"} p={4}>
        <VStack align="stretch" spacing={0}>
          {/* 历史记录时间线 */}
          {loading ? (
            <Flex justify="center" align="center" h="100px">
              <Spinner size="md" color="blue.500" />
            </Flex>
          ) : (
            <Box position="relative" pl={pxToRem(12)}>
              {/* 垂直时间线 */}
              <Box
                position="absolute"
                left={pxToRem(12)}
                top={pxToRem(5)}
                bottom={0}
                width="1px"
                bg="gray.300"
                zIndex={0}
              />
              <Box mb={4} position="relative">
                <Flex
                  align="center"
                  cursor="pointer"
                  position="relative"
                  zIndex={2}
                  py={1}
                  borderRadius="md"
                  top={pxToRem(-4)}
                >
                  <Box
                    w={pxToRem(30)}
                    h={pxToRem(30)}
                    borderRadius="md"
                    bg="#1D8AFF"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr={5}
                    ml={pxToRem(-14)}
                    color="#1D8AFF"
                  >
                    <Box
                      bg={"gray.100"}
                      width={pxToRem(10)}
                      height={pxToRem(10)}
                      borderRadius={6}
                    />
                  </Box>
                  <Box
                    onClick={() => {
                      setIsEditable(false); // 设置为不可编辑
                      setSelectedHistoryId(null);
                      setSelectedHistory(null);
                      setOutline(editData); // 清空历史数据
                      setCurrentSectionIndex(0)
                    }}
                    w={"100%"}
                    borderRadius={pxToRem(8)}
                    h={pxToRem(40)}
                    lineHeight={pxToRem(32)}
                    _hover={{ bg: "gray.50" }}
                    bg={!selectedHistoryId ? "gray.100" : "transparent"}
                    fontSize={14}
                    fontWeight="medium"
                    margin={0}
                    pl={1}
                  >
                    Current Version
                  </Box>
                </Flex>
              </Box>
              {historyByDate.map((dateGroup, dateIndex) => (
                <Box key={dateGroup.date} mb={4} position="relative">
                  <Flex
                    align="center"
                    cursor="pointer"
                    onClick={() => toggleDateGroup(dateIndex)}
                    position="relative"
                    zIndex={2}
                    py={1}
                    mt={pxToRem(20)}
                  >
                    <Box
                      w={pxToRem(30)}
                      h={pxToRem(30)}
                      borderRadius="md"
                      bg="gray.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={5}
                      ml={pxToRem(-14)}
                    >
                      {dateGroup.isOpen ? (
                        <ChevronDownIcon boxSize={pxToRem(14)} />
                      ) : (
                        <>-</>
                      )}
                    </Box>
                    <Text fontSize={14} fontWeight="medium" margin={0}>
                      {dateGroup.date}
                    </Text>
                  </Flex>

                  {dateGroup.isOpen && (
                    <VStack align="stretch" spacing={0} mt={1} ml={pxToRem(12)}>
                      
                      {dateGroup.histories.map((history) => (
                        <Flex
                          key={history.id}
                          align="center"
                          cursor="pointer"
                          onClick={() => handleSelectHistory(history.id)}
                          position="relative"
                          pl={pxToRem(40)}
                          mt={pxToRem(20)}
                          py={2}
                          bg={
                            selectedHistoryId === history.id
                              ? "gray.100"
                              : "transparent"
                          } 
                          _hover={{ bg: "gray.50" }}
                          borderRadius={pxToRem(8)}
                        >
                          <Box
                            position="absolute"
                            left={pxToRem(20)}
                            top="50%"
                            transform="translateY(-50%)"
                            w={pxToRem(8)}
                            h={pxToRem(8)}
                            borderRadius="full"
                            bg="orange.400"
                            zIndex={2}
                          />
                          <Text
                            fontSize="14"
                            fontWeight="medium"
                            padding={0}
                            margin={0}
                          >
                            {formatTime(history.created_at)}
                          </Text>
                          {/* <Box
                            ml={2}
                            px={1.5} 
                            py={0.5}
                            borderRadius="full"
                            bg="orange.100"
                            color="orange.800"
                            fontSize="xs"
                          >
                            Yc
                          </Box> */}
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default HistoryPanel;
